import { useSelector } from "react-redux";
import { RootState } from "../../../../store";
import { formMappings } from "../../../../config/formMappings";
import { FormEntry } from "../../../../types/types";

interface BazaarInfoTableProps {
  v2Entries: FormEntry[];
}

export default function BazaarInfoTable({ v2Entries }: BazaarInfoTableProps) {
  const entries = useSelector((state: RootState) => state.formEntries.entries);
  const activeFormName = useSelector(
    (state: RootState) => state.activeForm.activeFormName
  );
  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const isVersion1 = formMapping?.version === "1";
  const bazaarTableMapping =
    formMapping && "bazaarTable" in formMapping
      ? formMapping.bazaarTable?.fieldMappings
      : undefined;

  // Process entries based on version
  const bazaarRequests = (isVersion1 ? entries : v2Entries)
    .filter((entry) => {
      if (isVersion1) {
        return entry.meta["Will you be needing a table for bazaar?"] === "Yes";
      } else {
        // For v2+, use registeredForConference field from bazaarTable mapping
        if (!bazaarTableMapping) return false;
        const registeredField =
          entry.meta[bazaarTableMapping.registeredForConference] ||
          entry.meta["Will you be needing a table for bazaar?"];
        return registeredField === "Yes";
      }
    })
    .map((entry) => {
      if (isVersion1) {
        const paidStatus =
          entry.meta["Paid?"] ||
          entry.meta[formMapping?.fieldMappings?.paid || ""] ||
          "No";
        return {
          name: `${entry.meta["First Name"]?.[0] || "N/A"} ${
            entry.meta["Last Name"]?.[0] || "N/A"
          }`,
          companyName:
            entry.meta["Company Name"] || entry.meta["Company"] || "N/A",
          email: entry.meta["Email"]?.[0] || "N/A",
          phoneNumber: entry.meta["Phone"] || "N/A",
          description:
            entry.meta[
              "Please tell us more about the items you wish to sell"
            ] || "N/A",
          tables: parseInt(
            entry.meta["How many tables do you need? ($25/per table)"] || "0",
            10
          ),
          paid: paidStatus === "Yes" ? "Yes" : "No",
        };
      } else {
        // For v2+, use field mappings from bazaarTable
        if (!bazaarTableMapping) {
          return {
            name: "N/A",
            companyName: "N/A",
            email: "N/A",
            phoneNumber: "N/A",
            description: "N/A",
            tables: 0,
            paid: "No",
          };
        }

        const firstName =
          entry.meta[bazaarTableMapping.firstName] ||
          entry.meta["First Name"] ||
          "N/A";
        const lastName =
          entry.meta[bazaarTableMapping.lastName] ||
          entry.meta["Last Name"] ||
          "N/A";
        const companyName =
          entry.meta[bazaarTableMapping.companyName] ||
          entry.meta["Company Name"] ||
          entry.meta["Company"] ||
          "N/A";
        const email =
          entry.meta[bazaarTableMapping.email] || entry.meta["Email"] || "N/A";
        const phone =
          entry.meta[bazaarTableMapping.phone] || entry.meta["Phone"] || "N/A";
        const tables =
          entry.meta[bazaarTableMapping.numberOfTables] ||
          entry.meta["How many tables do you need? ($25/per table)"] ||
          "0";
        const description =
          entry.meta[bazaarTableMapping.description] ||
          entry.meta["Please tell us more about the items you wish to sell"] ||
          "N/A";
        const paidStatusRaw =
          entry.meta[bazaarTableMapping.paid] || entry.meta["Paid?"] || "No";
        const paidStatus = Array.isArray(paidStatusRaw)
          ? paidStatusRaw[0]
          : paidStatusRaw;

        return {
          name: `${Array.isArray(firstName) ? firstName[0] : firstName} ${
            Array.isArray(lastName) ? lastName[0] : lastName
          }`,
          companyName: Array.isArray(companyName)
            ? companyName[0]
            : companyName,
          email: Array.isArray(email) ? email[0] : email,
          phoneNumber: Array.isArray(phone) ? phone[0] : phone,
          description: Array.isArray(description)
            ? description[0]
            : description,
          tables: parseInt(Array.isArray(tables) ? tables[0] : tables, 10),
          paid: paidStatus === "Yes" ? "Yes" : "No",
        };
      }
    });

  return (
    <div className="overflow-y-scroll no-scrollbar rounded-lg bg-white shadow col-span-4">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            >
              Name
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Company Name
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Tables Needed
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Email
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Phone Number
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Description
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Paid
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {bazaarRequests.map((entry, idx) => (
            <tr key={idx}>
              <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                {entry.name}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {entry.companyName}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                {entry.tables}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <a href={`mailto:${entry.email}`} className="hover:underline">
                  {entry.email}
                </a>
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <a
                  href={`tel:${entry.phoneNumber}`}
                  className="hover:underline"
                >
                  {entry.phoneNumber}
                </a>
              </td>
              <td className="px-3 py-4 text-sm text-gray-500 whitespace-normal break-words">
                {entry.description}
              </td>
              <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                <span
                  className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium ${
                    entry.paid === "Yes"
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {entry.paid}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
